import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Image, MessageSquare, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const { token, organization } = useAuth();
  const [posts, setPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', image: '' });
  const [saving, setSaving] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    if (organization) fetchPosts();
  }, [organization]);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/${organization.org_id}`);
      if (res.ok) setPosts(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(prev => ({ ...prev, image: ev.target.result }));
    reader.readAsDataURL(file);
    imageRef.current.value = '';
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...form, org_id: organization.org_id })
      });
      if (res.ok) {
        toast.success('Post published!');
        setDialogOpen(false);
        setForm({ title: '', content: '', image: '' });
        fetchPosts();
      } else toast.error('Failed to create post');
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (postId) => {
    if (!confirm('Delete this post?')) return;
    try {
      const res = await fetch(`${API_URL}/api/blog/${postId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Post deleted'); fetchPosts(); }
    } catch { toast.error('Error'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="blog-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">Blog</h1>
            <p className="text-gray-600 mt-2">Share news and updates about your tournament</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="new-post-btn">
                <Plus size={18} className="mr-2" /> New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-black max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Create Blog Post</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Post title..." className="bg-gray-100 border-gray-200 text-black" data-testid="post-title" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Content</Label>
                  <Textarea value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} placeholder="Write your post..." className="bg-gray-100 border-gray-200 text-black min-h-[120px]" data-testid="post-content" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Image (optional)</Label>
                  {form.image && <img src={form.image} alt="Preview" className="w-full h-32 object-cover rounded-lg border border-gray-200" />}
                  <input type="file" ref={imageRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => imageRef.current?.click()} className="border-gray-200 text-gray-700">
                    <Image size={16} className="mr-2" /> {form.image ? 'Change Image' : 'Add Image'}
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-gray-200 text-black">Cancel</Button>
                  <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="publish-post-btn">
                    {saving ? 'Publishing...' : 'Publish'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {posts.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <MessageSquare className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-black text-lg mb-2">No blog posts yet</h3>
            <p className="text-gray-500">Create your first post to share tournament updates</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <Card key={post.post_id} className="bg-white border-gray-200">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-black text-xl font-bold mb-2">{post.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(post.created_at).toLocaleDateString()}</span>
                        <span>{post.author_name}</span>
                        <span className="flex items-center gap-1"><MessageSquare size={12} /> {post.comment_count || 0} comments</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(post.post_id)} className="text-gray-600 hover:text-red-500">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  {post.image && <img src={post.image} alt="" className="w-full h-48 object-cover rounded-lg mb-3" />}
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{post.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
